import { saveAs } from 'file-saver';
import { Download, Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { DataTable } from 'components/ui/data-table';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { Typography } from 'components/ui/typography';
import { Routes } from 'config/constants';

import { columns } from './columns';
import { DrawdownSettingsCard } from './components/DrawdownSettingsCard';
import { useBilling } from './hooks/useBilling';
import { fetchAllTransactions, useDrawdownTransactions } from './hooks/useDrawdownTransactions';

const Page = () => {
  const [downloading, setDownloading] = useState(false);
  const { drawdown, isLoading, error } = useBilling();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !error && drawdown && !drawdown.enabled) {
      navigate(Routes.DrawdownSetup, { replace: true });
    }
  }, [isLoading, error, drawdown, navigate]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const allTx = await fetchAllTransactions();
      const rows = [
        ['Date', 'Description', 'Amount', 'Currency', 'Transaction ID'],
        ...allTx.map((tx) => [
          new Date(tx.createdAt).toISOString().split('T')[0],
          `"${(tx.description ?? '').replace(/^[=+\-@]/, "'$&").replace(/"/g, '""')}"`,
          (tx.amount.amountSubunits / 100).toFixed(2),
          tx.amount.currencyCode,
          tx.id,
        ]),
      ];
      const csv = rows.map((r) => r.join(',')).join('\n');
      saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'transactions.csv');
    } catch {
      alert('Failed to download transactions. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const {
    transactions,
    hasNextPage,
    hasPreviousPage,
    loading: txLoading,
    error: txError,
    pageIndex,
    nextPage,
    previousPage,
  } = useDrawdownTransactions();

  return (
    <div>
      <div className="mt-[28px]">
        <DrawdownSettingsCard />
      </div>

      {drawdown?.enabled && (
        <Card className="mt-6">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>Transactions</CardTitle>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  CSV export is limited to the most recent 10,000 transactions. Contact support for
                  a full export.
                </TooltipContent>
              </Tooltip>
              <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading}>
                <Download className="mr-2 h-4 w-4" />
                {downloading ? 'Downloading...' : 'Download CSV'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={transactions}
              loading={txLoading}
              manualPagination
              pageIndex={pageIndex}
              pageSize={20}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              hideTotalPageCount
              hideToolbar
              onPaginationChange={(pagination) => {
                if (pagination.pageIndex > pageIndex) {
                  nextPage();
                } else if (pagination.pageIndex < pageIndex) {
                  previousPage();
                }
              }}
              emptyState={
                txError ? (
                  <Typography variant="body" as="span" className="text-destructive">
                    Failed to load transactions.
                  </Typography>
                ) : (
                  <Typography variant="small" as="span">
                    No transactions yet
                  </Typography>
                )
              }
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Page;
